import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { fetchMe, logout, api } from "@/lib/auth";
import {
  Mail,
  ArrowUpRight,
  Sparkles,
  Instagram,
  Facebook,
  Youtube,
  Phone,
  Clock,
  AtSign,
  UserPlus,
  Search,
  GitCompare,
  HeartHandshake,
  Camera,
  TrendingUp,
  ChevronRight,
  ChevronDown as ChevronDownIcon,
  Menu,
  FileText,
  Shield,
  Cookie,
  Scale,
  AlertCircle,
  Ban,
  MessageCircle,
  Package,
  ClipboardList,
  LogOut,
} from "lucide-react";
import { devLog } from "@/lib/log";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export const LOGO_URL = "/logo-transparent.png";
const STORY_IMG = "/ai-about-tamil-wedding.jpg";

/* ---------- Nav ---------- */
// All possible tabs. `visibleFor` controls which roles see each tab.
//   - "anon"   : visitor without a session
//   - "couple" : signed-in couple
//   - "vendor" : signed-in vendor (with or without an application)
//   - "admin"  : signed-in admin
// If a tab omits `visibleFor`, it's shown to everyone.
const NAV_TABS = [
  { id: "home", label: "Home", to: "/" },
  { id: "about", label: "About Us", to: "/about" },
  { id: "founder", label: "Meet the Founder", to: "/founder" },
  { id: "process", label: "Process", to: "/process" },
  { id: "contact", label: "Contact Us", to: "/contact" },
  { id: "policies", label: "Policies", to: "/policies" },
  {
    id: "vendor-listing",
    label: "Vendor Listing",
    to: "/vendor-listing",
    // Only signed-in couples/vendors/admin see the listing tab.
    visibleFor: ["couple", "vendor", "admin"],
  },
  {
    id: "my-home",
    label: "My Home",
    to: "/my-home",
    // My Home is the couple's saved-vendors + saved-media hub.
    // Admin sees it too for support purposes; vendors don't.
    visibleFor: ["couple", "admin"],
  },
  {
    id: "luxe-directory",
    label: "Dashboard",
    to: "/admin",
    // Dashboard is admin-only.
    visibleFor: ["admin"],
  },
  {
    id: "login",
    label: "Login",
    to: "/login",
    // Anonymous visitors see "Login"; signed-in users see the user pill instead.
    visibleFor: ["anon"],
  },
];

export const TopBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [vendorStatus, setVendorStatus] = useState(null);

  // Pull the current session (if any) so the nav can be role-aware.
  // /auth/me returns 401 for anonymous users — fetchMe swallows that and
  // returns null. For signed-in vendors we also fetch /auth/my-status
  // so we can gate the Vendor Listing tab on the application's approval
  // state (pending vendors are signed-in but don't get directory access
  // until an admin approves them).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await fetchMe();
      if (cancelled) return;
      setUser(me);
      if (me?.role === "vendor") {
        try {
          const r = await api.get("/auth/my-status");
          if (!cancelled) setVendorStatus(r.data?.vendor_status || null);
        } catch (e) {
          devLog("ld_topbar my-status failed:", e?.message);
        }
      } else {
        setVendorStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // "anon" for anonymous visitors; otherwise the user's role.
  const roleBucket = user?.role || "anon";
  const visibleTabs = NAV_TABS.filter((t) => {
    if (t.visibleFor && !t.visibleFor.includes(roleBucket)) return false;
    // Pending/rejected vendors don't get the Vendor Listing tab — they
    // can still sign in, see all the other pages, and edit/track their
    // application, but the directory only unlocks on approval.
    if (
      t.id === "vendor-listing"
      && roleBucket === "vendor"
      && vendorStatus !== "approved"
    ) {
      return false;
    }
    return true;
  });

  const handleSelect = async (tab) => {
    if (tab.to === "__signout__") {
      await logout();
      setUser(null);
      navigate("/");
      return;
    }
    navigate(tab.to);
  };

  const handleSignOut = async () => {
    await logout();
    setUser(null);
    toast("Signed out", { duration: 1800 });
    navigate("/");
  };

  return (
    <header
      data-testid="top-bar"
      className="relative z-20 flex items-center justify-end gap-6 px-6 md:px-12 pt-5 md:pt-7"
    >
      {/* Wordmark removed per user request — Menu sits flush right. */}

      <div className="flex items-center gap-2 md:gap-3">
        {user && (
          <UserPill user={user} onSignOut={handleSignOut} />
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild data-testid="nav-menu-trigger">
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2.5 px-5 py-2.5 text-[12px] tracking-[0.18em] uppercase border border-[var(--ld-ink)] bg-[var(--ld-ink)] text-[var(--ld-gold)] hover:text-[#e9d59a] hover:bg-black focus:outline-none transition-colors duration-300"
              aria-label="Open navigation menu"
            >
              <Menu size={14} strokeWidth={1.6} />
              <span>Menu</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            data-testid="nav-menu-content"
            align="end"
            sideOffset={10}
            className="min-w-[220px] p-1 rounded-none border-[var(--ld-ink)] bg-[var(--ld-ink)] text-[var(--ld-gold)] shadow-[0_12px_40px_rgba(44,42,41,0.25)]"
          >
            {visibleTabs.map((tab) => (
              <DropdownMenuItem
                key={tab.id}
                data-testid={`nav-menu-item-${tab.id}`}
                onSelect={() => handleSelect(tab)}
                data-active={location.pathname === tab.to ? "true" : undefined}
                className="px-4 py-3 text-[12px] tracking-[0.18em] uppercase text-[var(--ld-gold)] cursor-pointer rounded-none focus:bg-black focus:text-[#e9d59a] data-[highlighted]:bg-black data-[highlighted]:text-[#e9d59a] data-[active=true]:text-[#e9d59a]"
              >
                {tab.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

/* ---------- Signed-in user pill (top right) ---------- */
export const UserPill = ({ user, onSignOut }) => {
  const email = user?.email || "";
  const initial = (email || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      data-testid="user-pill"
      className="inline-flex items-center gap-2 pl-1 pr-1.5 py-1 border border-[var(--ld-gold-deep)]/40 bg-[var(--ld-ivory)]/80 backdrop-blur-sm rounded-full text-[var(--ld-ink)] shadow-[0_1px_0_rgba(44,42,41,0.04)]"
    >
      <span
        aria-hidden
        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[var(--ld-ink)] text-[var(--ld-gold)] font-serif-display text-[13px]"
      >
        {initial}
      </span>
      <span
        data-testid="user-pill-email"
        className="hidden sm:inline-block max-w-[180px] truncate text-[11px] tracking-[0.06em] text-[var(--ld-ink)]"
        title={email}
      >
        {email}
      </span>
      <button
        type="button"
        data-testid="user-pill-signout"
        onClick={onSignOut}
        aria-label="Sign out"
        title="Sign out"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] hover:bg-[var(--ld-gold)]/15 transition-colors"
      >
        <LogOut size={13} strokeWidth={1.6} />
      </button>
    </div>
  );
};

/* ---------- Footer ---------- */
export const Footer = () => (
  <footer
    data-testid="footer"
    className="border-t border-[var(--ld-border)]/80 px-6 md:px-12 py-10 mt-10"
  >
    <div className="mx-auto max-w-7xl flex flex-col md:flex-row items-center justify-between gap-4 text-[var(--ld-ink-muted)]">
      <Link to="/" className="flex items-center gap-3">
        <img
          src={LOGO_URL}
          alt=""
          aria-hidden
          className="h-8 w-8 object-contain"
        />
        <span className="font-serif-display text-lg text-[var(--ld-ink)]">
          Luxé Directory
        </span>
      </Link>
      <div className="text-xs tracking-[0.2em] uppercase">
        © {new Date().getFullYear()} · All rights quietly reserved
      </div>
    </div>
  </footer>
);

/* ---------- Page heading helper ---------- */
const PageEyebrow = ({ overline, heading, sub }) => (
  <div className="text-center max-w-2xl mx-auto">
    <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
      {overline}
    </span>
    <h1 className="font-serif-display mt-4 text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-[var(--ld-ink)] font-medium">
      {heading}
    </h1>
    {sub && (
      <p className="mt-4 text-[15px] md:text-base leading-relaxed text-[var(--ld-ink-muted)]">
        {sub}
      </p>
    )}
    <div className="mx-auto mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />
  </div>
);

/* ---------- About ---------- */
export const AboutSection = () => (
  <section
    id="about"
    data-testid="story-section"
    className="relative px-6 md:px-12 py-20 md:py-28"
  >
    <div className="mx-auto grid max-w-7xl grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
      <div className="md:col-span-7 md:pr-8">
        <span className="font-accent text-[11px] text-[var(--ld-gold-deep)]">
          About Us
        </span>
        <h2
          data-testid="story-heading"
          className="font-serif-display mt-5 text-3xl sm:text-4xl md:text-5xl leading-[1.08] tracking-tight text-[var(--ld-ink)] font-medium max-w-[20ch]"
        >
          The Tamil Yellow Book for weddings —{" "}
          <span className="italic">reimagined for today&apos;s couples.</span>
        </h2>
        <div className="mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />

        <div
          data-testid="about-body"
          className="mt-6 space-y-5 text-[15px] md:text-base leading-[1.85] text-[var(--ld-ink-muted)] max-w-xl"
        >
          <p>
            Luxé Directory was created to simplify wedding planning by
            bringing trusted vendors under one roof — with a strong focus on
            Tamil-based professionals, while remaining inclusive of weddings
            from all cultural backgrounds.
          </p>
          <p>
            Within the Tamil community, there has never been a dedicated
            wedding directory. For couples planning a Hindu wedding, fusion
            wedding or wedding receptions, knowing where to start can feel
            overwhelming. Luxé Directory exists to change that — offering
            clarity, guidance, and a helping hand from the very first step.
            While Luxé Directory provides guidance and recommendations, we are
            not an event planning service — our role is to help you discover,
            compare, and connect with the right vendors for your wedding.
          </p>
          <p>
            We are a specialised marketplace and consultancy hybrid, designed
            to connect couples with vendors who understand cultural traditions
            while also embracing modern and multicultural celebrations.
            Whether you&apos;re planning a traditional ceremony or a fusion
            wedding, Luxé Directory helps you discover the right talent with
            ease.
          </p>
          <p>
            Every vendor featured has an equal opportunity to showcase their
            work, ensuring fairness, visibility, and quality across the
            platform. Our goal is not just to list services, but to build a
            trusted space where couples can confidently find the
            professionals who will bring their vision to life.
          </p>
          <p className="font-serif-display italic text-lg text-[var(--ld-ink)] pt-2 border-t border-[var(--ld-border)]/70">
            Simply put, Luxé Directory is the Tamil Yellow Book for weddings —
            reimagined for today&apos;s couples.
          </p>
        </div>
      </div>

      <div className="md:col-span-5 relative">
        <div className="md:sticky md:top-24">
          <div
            data-testid="story-image-wrap"
            className="relative aspect-[3/4] w-full overflow-hidden"
          >
            <img
              src={STORY_IMG}
              alt="Tamil Hindu wedding ceremony — AI-generated"
              className="h-full w-full object-cover transition-transform duration-[1800ms] ease-out hover:scale-[1.04]"
            />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-[var(--ld-gold)]/25" />
          </div>
          <p
            data-testid="story-image-caption"
            className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)] font-accent text-center"
          >
            Image generated using AI
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Process ---------- */
const COUPLES_FLOW_STEPS = [
  {
    Icon: UserPlus,
    step: "01",
    title: "Sign Up",
    desc: "Create a free account in moments — no hidden sign up fees",
  },
  {
    Icon: ClipboardList,
    step: "02",
    title: "Form",
    desc: "Share your wedding details so we can match you with the right vendors.",
  },
  {
    Icon: MessageCircle,
    step: "03",
    title: "Free Consultation",
    desc: "Book a complimentary intro chat with Luxé Directory to walk through your vision.",
  },
  {
    Icon: Search,
    step: "04",
    title: "Discover",
    desc: "Browse a curated edit of trusted vendors.",
  },
  {
    Icon: GitCompare,
    step: "05",
    title: "Compare",
    desc: "Shortlist favourites and compare offerings side by side.",
  },
  {
    Icon: HeartHandshake,
    step: "06",
    title: "Connect",
    desc: "Reach out directly to the vendors who feel right.",
  },
];

const VENDORS_FLOW_STEPS = [
  {
    Icon: UserPlus,
    step: "01",
    title: "Sign Up",
    desc: "Create a free account to start your application.",
  },
  {
    Icon: ClipboardList,
    step: "02",
    title: "Form",
    desc: "Tell us about your services, pricing, coverage and portfolio.",
  },
  {
    Icon: MessageCircle,
    step: "03",
    title: "Free Consultation",
    desc: "We meet you for a complimentary intro chat to align on fit and goals.",
  },
  {
    Icon: Package,
    step: "04",
    title: "Choose a Package",
    desc: "Pick the listing package that fits your business.",
  },
  {
    Icon: Camera,
    step: "05",
    title: "Showcase",
    desc: "Build a beautiful page with portfolio, services and pricing.",
  },
  {
    Icon: Sparkles,
    step: "06",
    title: "Get Matched",
    desc: "Couples planning a wedding like yours discover your work.",
  },
  {
    Icon: TrendingUp,
    step: "07",
    title: "Grow",
    desc: "Receive enquiries from couples",
  },
];

const ProcessFlow = ({ steps, testId }) => {
  const n = steps.length;
  // Responsive grid: 1 col on mobile, 2 on sm, scales up to ~n on lg.
  // We keep large rows from feeling lopsided: 6 → 3-cols (2 rows of 3),
  // 7 → 4-cols (4+3), everything else falls back to a sensible default.
  const lgCols =
    n <= 3
      ? "lg:grid-cols-3"
      : n === 4
      ? "lg:grid-cols-4"
      : n === 5
      ? "lg:grid-cols-5"
      : n === 6
      ? "lg:grid-cols-3"
      : "lg:grid-cols-4";
  return (
    <ol
      data-testid={testId}
      className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ${lgCols} items-stretch gap-4`}
    >
      {steps.map(({ Icon, step, title, desc }, i) => (
        <li
          key={step}
          data-testid={`${testId}-step-${step}`}
          className="relative h-full"
        >
          <div className="h-full bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] p-6 flex flex-col gap-3 transition-colors duration-500 hover:bg-black hover:text-[#e9d59a]">
            <div className="flex items-center justify-between">
              <Icon size={20} strokeWidth={1.4} />
              <span className="font-accent text-[10px] opacity-70">{step}</span>
            </div>
            <h3 className="font-serif-display text-2xl leading-none mt-2">
              {title}
            </h3>
            <p className="text-[13px] leading-relaxed text-[#d8c79c]/85">
              {desc}
            </p>
          </div>
          {i < n - 1 && (
            <div className="md:hidden flex justify-center py-2 text-[var(--ld-gold-deep)]">
              <ChevronDownIcon size={18} strokeWidth={1.5} />
            </div>
          )}
        </li>
      ))}
    </ol>
  );
};

export const ProcessSection = () => (
  <section
    id="process"
    data-testid="process-section"
    className="relative px-6 md:px-12 py-20 md:py-28"
  >
    <div className="mx-auto max-w-7xl">
      <PageEyebrow
        overline="The Process"
        heading="How it works."
        sub="Two journeys, one directory — designed to feel effortless on both sides."
      />

      <div className="mt-16">
        <div className="flex items-center gap-4 mb-6">
          <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
            For Couples
          </span>
          <div className="flex-1 h-px bg-[var(--ld-border)]" />
        </div>
        <ProcessFlow
          testId="couples-flow"
          steps={COUPLES_FLOW_STEPS}
        />
      </div>

      <div className="mt-20">
        <div className="flex items-center gap-4 mb-6">
          <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
            For Vendors
          </span>
          <div className="flex-1 h-px bg-[var(--ld-border)]" />
        </div>
        <ProcessFlow
          testId="vendors-flow"
          steps={VENDORS_FLOW_STEPS}
        />
      </div>
    </div>
  </section>
);

/* ---------- Contact ---------- */
const TikTokIcon = ({ size = 18, strokeWidth = 1.5, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const SOCIALS = [
  {
    id: "instagram",
    label: "Instagram",
    href: "https://instagram.com/Luxe.directory",
    Icon: Instagram,
  },
  {
    id: "tiktok",
    label: "TikTok",
    href: "https://www.tiktok.com/@lux.directory?_r=1&_t=ZN-96FqSHtlAs2",
    Icon: TikTokIcon,
  },
  {
    id: "facebook",
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61584282334210",
    Icon: Facebook,
  },
  {
    id: "youtube",
    label: "YouTube",
    href: "https://youtube.com/@Luxe.directory",
    Icon: Youtube,
  },
];

export const ContactSection = () => {
  const [contact, setContact] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, subject, message } = contact;
    if (!name.trim() || !subject.trim() || !message.trim()) {
      toast("Please fill in every field", { duration: 2500 });
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast("Please enter a valid email", { duration: 2500 });
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contact`,
        contact
      );
      toast("Message received", {
        description: "Thank you — we'll be in touch soon.",
        duration: 3200,
      });
      setContact({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast("Couldn't send. Please try again.", {
        description: err?.response?.data?.detail || "Network error.",
        duration: 3200,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative px-6 md:px-12 py-20 md:py-28"
    >
      <div className="mx-auto max-w-6xl">
        <PageEyebrow
          overline="Contact Us"
          heading="We're here to help."
          sub="Fill out the form below to reach us. Whether you're a couple with a question or a vendor curious about joining, we'd love to hear from you."
        />

        <form
          data-testid="contact-form"
          onSubmit={handleSubmit}
          className="mt-12 mx-auto max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          <div className="sm:col-span-1">
            <label
              htmlFor="contact-name"
              className="block text-[11px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)] mb-2"
            >
              Name
            </label>
            <input
              id="contact-name"
              data-testid="contact-name"
              type="text"
              value={contact.name}
              onChange={(e) => setContact({ ...contact, name: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border border-[var(--ld-ink)]/30 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors duration-500"
            />
          </div>
          <div className="sm:col-span-1">
            <label
              htmlFor="contact-email"
              className="block text-[11px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)] mb-2"
            >
              Email
            </label>
            <input
              id="contact-email"
              data-testid="contact-email"
              type="email"
              value={contact.email}
              onChange={(e) => setContact({ ...contact, email: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border border-[var(--ld-ink)]/30 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors duration-500"
            />
          </div>
          <div className="sm:col-span-2">
            <label
              htmlFor="contact-subject"
              className="block text-[11px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)] mb-2"
            >
              Subject
            </label>
            <input
              id="contact-subject"
              data-testid="contact-subject"
              type="text"
              value={contact.subject}
              onChange={(e) => setContact({ ...contact, subject: e.target.value })}
              className="w-full h-12 px-4 bg-transparent border border-[var(--ld-ink)]/30 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors duration-500"
            />
          </div>
          <div className="sm:col-span-2">
            <label
              htmlFor="contact-message"
              className="block text-[11px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)] mb-2"
            >
              Message
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-message"
              rows={6}
              value={contact.message}
              onChange={(e) => setContact({ ...contact, message: e.target.value })}
              className="w-full px-4 py-3 bg-transparent border border-[var(--ld-ink)]/30 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors duration-500 resize-none"
            />
          </div>
          <div className="sm:col-span-2 flex justify-center mt-2">
            <button
              data-testid="contact-submit"
              type="submit"
              disabled={sending}
              className="inline-flex items-center justify-center gap-2 px-10 py-3.5 text-[12px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] transition-colors duration-500 hover:text-[#e9d59a] hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Send now"}
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </button>
          </div>
        </form>

        <div className="mt-20 text-center">
          <p
            data-testid="socials-line"
            className="text-[15px] md:text-base text-[var(--ld-ink-muted)] max-w-2xl mx-auto"
          >
            You can also find us on Instagram, TikTok, Facebook and YouTube.
          </p>
          <ul
            data-testid="socials-list"
            className="mt-6 flex items-center justify-center gap-3"
          >
            {SOCIALS.map(({ id, label, href, Icon }) => (
              <li key={id}>
                <a
                  data-testid={`social-${id}`}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${label} — @Luxe.directory`}
                  title={`${label} · @Luxe.directory`}
                  className="group inline-flex h-12 w-12 items-center justify-center border border-[var(--ld-ink)] bg-[var(--ld-ink)] text-[var(--ld-gold)] transition-colors duration-300 hover:text-[#e9d59a] hover:bg-black"
                >
                  <Icon size={18} strokeWidth={1.6} />
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
            @Luxe.directory
          </p>
        </div>

        <div
          data-testid="contact-details"
          className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 border-t border-[var(--ld-border)]/70 pt-12"
        >
          {[
            {
              k: "phone",
              Icon: Phone,
              label: "Phone",
              value: "07926536724",
              href: "tel:07926536724",
            },
            {
              k: "email",
              Icon: AtSign,
              label: "Email",
              value: "Luxedirectory@outlook.com",
              href: "mailto:Luxedirectory@outlook.com",
            },
            {
              k: "hours",
              Icon: Clock,
              label: "Business hours",
              value: "Monday – Sunday · 24/7",
              href: null,
            },
          ].map(({ k, Icon, label, value, href }) => (
            <div
              key={k}
              data-testid={`contact-detail-${k}`}
              className="text-center sm:text-left"
            >
              <div className="flex items-center justify-center sm:justify-start gap-2 text-[var(--ld-gold-deep)]">
                <Icon size={14} strokeWidth={1.5} />
                <span className="font-accent text-[10px]">{label}</span>
              </div>
              <div className="mt-3 font-serif-display text-xl text-[var(--ld-ink)]">
                {href ? (
                  <a
                    href={href}
                    className="hover:text-[var(--ld-gold-deep)] transition-colors duration-300"
                  >
                    {value}
                  </a>
                ) : (
                  value
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Policies ---------- */
const POLICIES = [
  {
    id: "vendor-tcs",
    title: "Vendor Terms & Conditions",
    Icon: FileText,
    body: [
      "These terms govern the relationship between Luxé Directory and any vendor listed on the platform.",
      "Vendors are responsible for the accuracy of all information they publish, the legality of services they offer, and ensuring fair pricing and conduct in any dealings with couples introduced through the directory.",
      "By accepting a listing, vendors agree to respond to enquiries within a reasonable time frame and to honour any commitments made through the platform.",
    ],
  },
  {
    id: "privacy",
    title: "Privacy Policy (GDPR-compliant)",
    Icon: Shield,
    body: [
      "Luxé Directory collects only the personal data needed to operate the service — typically your name, email address, and any details you submit through forms.",
      "We process your data lawfully, transparently, and only for the purposes you've consented to. You have the right to access, correct, export, or delete your data at any time by contacting Luxedirectory@outlook.com.",
      "We do not sell personal data to third parties. Where service providers (e.g. hosting, analytics) process data on our behalf, they do so under written agreements that comply with the UK GDPR.",
    ],
  },
  {
    id: "tcs",
    title: "Terms & Conditions",
    Icon: Scale,
    body: [
      "By using Luxé Directory you agree to use the platform lawfully and respectfully, and acknowledge that the directory is provided as an introduction service — not a guarantee of any outcome.",
      "Content on the site is provided for information only and may change without notice. Any reliance you place on it is at your own risk.",
      "These terms are governed by the laws of England and Wales.",
    ],
  },
  {
    id: "vendor-removal",
    title: "Right to remove or suspend vendors",
    Icon: Ban,
    body: [
      "Luxé Directory reserves the right to remove or suspend any vendor listing at its sole discretion, with or without notice.",
      "Reasons may include — but are not limited to — breach of these policies, repeated complaints from couples, misleading information, illegal conduct, or behaviour inconsistent with the standards of the platform.",
      "Where appropriate, we will notify the vendor and offer a fair opportunity to respond before any permanent removal.",
    ],
  },
  {
    id: "cookies",
    title: "Cookie Policy",
    Icon: Cookie,
    body: [
      "We use a small number of essential cookies to keep the site running and to remember your preferences.",
      "Optional analytics cookies help us understand how the directory is used so we can improve it. These are only set with your consent and you can withdraw consent at any time.",
      "You can control cookies through your browser settings. Disabling essential cookies may affect functionality.",
    ],
  },
  {
    id: "disclaimer",
    title: "Disclaimer",
    Icon: AlertCircle,
    bullets: [
      "Luxé Directory does not guarantee vendor quality. Listings are introductions, not endorsements.",
      "Luxé Directory is not liable for any disputes arising between couples and vendors. Any contract is between the couple and the vendor directly.",
      "Luxé Directory does not verify every vendor unless explicitly stated against an individual listing.",
    ],
  },
];

export const PoliciesSection = () => (
  <section
    id="policies"
    data-testid="policies-section"
    className="relative px-6 md:px-12 py-20 md:py-28"
  >
    <div className="mx-auto max-w-4xl">
      <PageEyebrow
        overline="Policies"
        heading="Our promises, plainly stated."
        sub="The terms that protect couples, vendors and Luxé Directory alike."
      />

      <Accordion
        data-testid="policies-accordion"
        type="single"
        collapsible
        className="mt-12 w-full"
      >
        {POLICIES.map(({ id, title, Icon, body, bullets }) => (
          <AccordionItem
            key={id}
            value={id}
            data-testid={`policy-${id}`}
            className="border-b border-[var(--ld-border)] last:border-b-0"
          >
            <AccordionTrigger className="py-6 text-left hover:no-underline group">
              <span className="flex items-center gap-4 flex-1 pr-4">
                <span className="flex h-10 w-10 items-center justify-center bg-[var(--ld-ink)] text-[var(--ld-gold)] shrink-0">
                  <Icon size={16} strokeWidth={1.5} />
                </span>
                <span className="font-serif-display text-xl md:text-2xl text-[var(--ld-ink)] leading-tight group-hover:text-[var(--ld-gold-deep)] transition-colors duration-300">
                  {title}
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="pl-14 pr-2 space-y-4 text-[14px] md:text-[15px] leading-[1.85] text-[var(--ld-ink-muted)]">
                {body && body.map((p, i) => <p key={`${i}-${p.slice(0, 24)}`}>{p}</p>)}
                {bullets && (
                  <ul className="space-y-3">
                    {bullets.map((b, i) => (
                      <li key={`${i}-${b.slice(0, 24)}`} className="flex items-start gap-3">
                        <span
                          aria-hidden
                          className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--ld-gold-deep)] shrink-0"
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* ---------- Notify strip (homepage only) ---------- */
export const NotifyStrip = () => {
  const [email, setEmail] = useState("");

  const handleNotify = (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast("Please enter a valid email", { duration: 2500 });
      return;
    }
    toast("You're on the list", {
      description: "We'll let you know the moment we open the doors.",
      duration: 3200,
    });
    setEmail("");
  };

  return (
    <section
      data-testid="notify-section"
      className="relative px-6 md:px-12 pb-32 md:pb-40"
    >
      <div className="mx-auto max-w-4xl text-center">
        <h3 className="font-serif-display mt-4 text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-[var(--ld-ink)] font-medium">
          Be the first to step inside.
        </h3>
        <form
          data-testid="notify-form"
          onSubmit={handleNotify}
          className="mt-8 mx-auto flex w-full max-w-xl flex-col sm:flex-row items-stretch gap-3 sm:gap-0"
        >
          <div className="relative flex-1">
            <Mail
              size={15}
              strokeWidth={1.5}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ld-ink-muted)]"
            />
            <input
              data-testid="notify-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full h-12 pl-11 pr-4 bg-transparent border border-[var(--ld-ink)]/30 sm:border-r-0 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors duration-500"
            />
          </div>
          <button
            data-testid="notify-submit"
            type="submit"
            className="h-12 px-7 text-sm tracking-wide bg-[var(--ld-ink)] text-[var(--ld-ivory)] border border-[var(--ld-ink)] transition-colors duration-500 hover:bg-[var(--ld-gold-deep)] hover:border-[var(--ld-gold-deep)]"
          >
            Notify me
          </button>
        </form>
        <p className="mt-4 text-xs text-[var(--ld-ink-muted)]">
          One email. When the doors open. Never a newsletter.
        </p>
      </div>
    </section>
  );
};

/* ---------- Page shell ---------- */
export const PageShell = ({ children, testId }) => (
  <main
    data-testid={testId}
    className="relative min-h-[100dvh] w-full overflow-x-hidden flex flex-col"
    style={{ backgroundColor: "var(--ld-ivory)" }}
  >
    <TopBar />
    <div className="flex-1">{children}</div>
    <Footer />
  </main>
);
